/* ============================================================
   productOptionsView — seller-defined priced options (a1a3b8f4)
   Pure mirror of the backend ProductOptionRules math:
   boolean fixed/per-unit, choice delta, quantity ×N absolute.
   ============================================================ */

import type { ProductOption } from '../types/product';

export const OPTION_TYPE = { boolean: 0, choice: 1, quantity: 2 } as const;
export const PRICE_MODE = { fixed: 0, perUnit: 1 } as const;

/** One customer selection (wire shape of ProductOptionSelectionItemDto). */
export interface OptionSelection {
  optionId: string;
  valueId?: string | null;
  quantity?: number | null;
}

export interface OptionPricingItem {
  optionId: string;
  name: string;
  valueLabel: string | null;
  quantity: number;
  addRub: number;
  perUnit: boolean;
}

export interface OptionPricing {
  items: OptionPricingItem[];
  perUnitAddRub: number;
  fixedAddRub: number;
}

export const EMPTY_OPTION_PRICING: OptionPricing = {
  items: [],
  perUnitAddRub: 0,
  fixedAddRub: 0,
};

export function optionUnitPrice(base: number, pricing: OptionPricing): number {
  return base + pricing.perUnitAddRub;
}

export function optionLineTotal(base: number, pricing: OptionPricing, quantity: number): number {
  return optionUnitPrice(base, pricing) * quantity + pricing.fixedAddRub;
}

function formatAdd(rub: number): string {
  return `+${Number.isInteger(rub) ? rub : rub.toFixed(2)} ₽`;
}

export function optionSummary(pricing: OptionPricing): string {
  return pricing.items
    .map((i) => {
      const label = i.valueLabel
        ? `${i.name}: ${i.valueLabel}`
        : i.quantity !== 1
          ? `${i.name} ×${i.quantity}`
          : i.name;
      return `${label} (${formatAdd(i.addRub)})`;
    })
    .join(' · ');
}

/** Resolve selections against the live option rows (all rules mirrored). */
export function computeOptionPricing(
  options: ProductOption[] | null | undefined,
  selections: OptionSelection[],
): OptionPricing {
  if (!options || selections.length === 0) return EMPTY_OPTION_PRICING;

  const byId = new Map(options.map((o) => [o.optionId, o]));
  const items: OptionPricingItem[] = [];
  let perUnitAdd = 0;
  let fixedAdd = 0;

  for (const selection of selections) {
    const option = byId.get(selection.optionId);
    if (!option || !option.enabled) continue;

    if (option.type === OPTION_TYPE.boolean) {
      const add = option.priceRub;
      const perUnit = option.priceMode === PRICE_MODE.perUnit;
      if (perUnit) perUnitAdd += add; else fixedAdd += add;
      items.push({ optionId: option.optionId, name: option.name, valueLabel: null, quantity: 1, addRub: add, perUnit });
    } else if (option.type === OPTION_TYPE.choice) {
      const value = (option.values ?? []).find((v) => v.valueId === selection.valueId);
      if (!value) continue;
      const add = value.priceRub;
      const perUnit = option.priceMode === PRICE_MODE.perUnit;
      if (perUnit) perUnitAdd += add; else fixedAdd += add;
      items.push({ optionId: option.optionId, name: option.name, valueLabel: value.label, quantity: 1, addRub: add, perUnit });
    } else {
      const qty = Math.max(1, Math.min(100, selection.quantity ?? 1));
      const add = option.priceRub * qty;
      fixedAdd += add;
      items.push({ optionId: option.optionId, name: option.name, valueLabel: null, quantity: qty, addRub: add, perUnit: false });
    }
  }

  return { items, perUnitAddRub: perUnitAdd, fixedAddRub: fixedAdd };
}

/** Selections → wire payload (only actually chosen rows ride the request). */
export function toWireSelections(
  selections: OptionSelection[],
): { optionId: string; valueId?: string; quantity?: number }[] {
  return selections.map((s) => ({
    optionId: s.optionId,
    ...(s.valueId ? { valueId: s.valueId } : {}),
    ...(s.quantity != null ? { quantity: s.quantity } : {}),
  }));
}

/**
 * The chosen color for the shop viewer card: a Choice option named
 * «цвет»/«color» whose selected value feeds the 3D tint / swatch.
 */
export function selectedColorLabel(
  options: ProductOption[] | null | undefined,
  selections: OptionSelection[],
): string | null {
  if (!options) return null;
  const colorOption = options.find(
    (o) => o.enabled && o.type === OPTION_TYPE.choice && /цвет|color/i.test(o.name),
  );
  if (!colorOption) return null;
  const selection = selections.find((s) => s.optionId === colorOption.optionId);
  if (!selection?.valueId) return null;
  return (colorOption.values ?? []).find((v) => v.valueId === selection.valueId)?.label ?? null;
}
